import { Feature } from "@omni/types/api/product/bff/shared/feature.model";
import { ProductTileFeature } from "@omni/types/api/product/bff/shared/productTileFeature.model";
import { CloudFeature } from "@omni/types/api/product/cloud/shared/feature.model";
import { AlgoliaSearchFeature } from "@omni/types/api/search/algolia/product/shared/feature.model";
import { BffSearchFeature } from "@omni/types/api/search/bff/product/shared/feature.model";
import { ModelMapper } from "@omni/types/utils/modelMapper";

import { FeatureKindMapper } from "./featureKind.mapper";
import { FeatureTooltipImageMapper } from "./featureToolTipImage.mapper";
import { FeatureUnitMapper } from "./featureUnit.mapper";
import { FeatureValueMapper } from "./featureValue.mapper";
import { RangeSetMapper } from "./rangeSet.mapper";

const fromCloudToBff: ModelMapper<CloudFeature, Feature> = (model) => {
    return {
        name: model.name,
        code: model.code,
        featureValues: model.featureValues.map(
            FeatureValueMapper.fromCloudToBff
        ),
        toolTipImage: FeatureTooltipImageMapper.fromCloud(model.toolTipImage),
        kind: FeatureKindMapper.fromCloudToBff(model.kind),
        unit: FeatureUnitMapper.fromCloudtoBff(model.unit),
        coreFeature: model.coreFeature,
        distinctiveFeature: model.distinctiveFeature,
        id: model.id,
        label: model.label,
        description: model.description,
        rangeSet: RangeSetMapper.fromCloud(model.rangeSet ?? null)
    } satisfies Feature;
};

const fromAlgoliaSearchToProductTile: ModelMapper<
    AlgoliaSearchFeature,
    ProductTileFeature
> = (model) => {
    return {
        name: model.name,
        code: model.code,
        featureValues: model.values.map(
            FeatureValueMapper.fromAlgoliaSearchToBff
        ),
        toolTipImage: null,
        kind: FeatureKindMapper.fromAlgoliaSearchFeatureToBff(model),
        unit: FeatureUnitMapper.fromAlgoliaSearchToProductTile(model.unit)
    } satisfies ProductTileFeature;
};

const fromAlgoliaSearchToBffSearch: ModelMapper<
    AlgoliaSearchFeature,
    BffSearchFeature
> = (model) => {
    return {
        name: model.name,
        code: model.code,
        ranking: model.ranking,
        values: model.values,
        unit: model.unit
    } satisfies BffSearchFeature;
};

const fromBffSearchToProductTile: ModelMapper<
    BffSearchFeature,
    ProductTileFeature
> = (model) => {
    return {
        name: model.name,
        code: model.code,
        featureValues: model.values.map(FeatureValueMapper.fromBffSearchToBff),
        toolTipImage: null,
        kind: FeatureKindMapper.fromBffSearchFeatureToBff(model),
        unit: FeatureUnitMapper.fromBffSearchToProductTile(model.unit)
    } satisfies ProductTileFeature;
};

const fromBffSearchToAlgoliaSearch: ModelMapper<
    BffSearchFeature,
    AlgoliaSearchFeature
> = (model) => {
    return {
        name: model.name,
        code: model.code,
        ranking: model.ranking,
        values: model.values,
        unit: model.unit
    } satisfies AlgoliaSearchFeature;
};

const fromProductTileToAlgoliaSearch: ModelMapper<
    ProductTileFeature,
    AlgoliaSearchFeature
> = (model) => {
    return {
        name: model.name,
        code: model.code,
        ranking: model.kind.sortOrder,
        unit: model.unit?.name || model.unit?.code || "",
        values: model.featureValues.map(
            FeatureValueMapper.fromBffToAlgoliaSearch
        )
    } satisfies AlgoliaSearchFeature;
};

const fromProductTileToBffSearch: ModelMapper<
    ProductTileFeature,
    BffSearchFeature
> = (model) => {
    return {
        name: model.name,
        code: model.code,
        ranking: model.kind.sortOrder,
        unit: model.unit?.name || model.unit?.code || "",
        values: model.featureValues.map(FeatureValueMapper.fromBffToBffSearch)
    } satisfies BffSearchFeature;
};

export const FeatureMapper = {
    fromCloudToBff: fromCloudToBff,
    fromAlgoliaSearchToProductTile: fromAlgoliaSearchToProductTile,
    fromAlgoliaSearchToBffSearch: fromAlgoliaSearchToBffSearch,
    fromBffSearchToProductTile: fromBffSearchToProductTile,
    fromBffSearchToAlgoliaSearch: fromBffSearchToAlgoliaSearch,
    fromProductTileToAlgoliaSearch: fromProductTileToAlgoliaSearch,
    fromProductTileToBffSearch: fromProductTileToBffSearch
};
